import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  DialogRoot,
  DialogTrigger,
  Input,
} from '@ibsys/kit';
import { FC, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { UpdateProfileRequest, useUpdateMyProfile } from '@/entities/profile';
import { ResetPasswordDialog } from '@/pages/profile/ui/ResetPasswordDialog';
import { EMAIL_REGEX } from '@/shared/lib';

export type MainInfoFormProps = {
  defaultValues: UpdateProfileRequest;
};

export const MainInfoForm: FC<MainInfoFormProps> = ({ defaultValues }) => {
  const { t } = useTranslation(['common', 'profile']);

  const [isPasswordDialogOpen, setPasswordDialogOpen] = useState(false);

  const { mutateAsync: updateProfile, isPending: isSavingProfile } = useUpdateMyProfile();

  const methods = useForm<UpdateProfileRequest>({ defaultValues, mode: 'onBlur' });

  const { reset, handleSubmit, control } = methods;

  useMemo(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const submit = handleSubmit((values) => updateProfile(values));

  return (
    <DialogRoot onOpenChange={setPasswordDialogOpen} open={isPasswordDialogOpen}>
      <Card className={'w-full'}>
        <CardHeader className={'flex gap-3 items-center h-13'}>
          <CardTitle>{t('profile:mainInformation')}</CardTitle>
          <DialogTrigger asChild>
            <Button className={'ml-auto'} variant={'outline'}>
              {t('profile:resetPassword')}
            </Button>
          </DialogTrigger>
        </CardHeader>
        <CardContent className={'flex flex-col gap-3'}>
          <FormProvider {...methods}>
            <Input label={t('profile:name')} control={control} name={'firstName'} />
            <Input label={t('profile:surname')} control={control} name={'lastName'} />
            <Input
              label={t('profile:email')}
              control={control}
              name={'email'}
              rules={{
                pattern: {
                  value: EMAIL_REGEX,
                  message: t('profile:errors.email'),
                },
              }}
            />
            <div className={'flex gap-3 mt-2'}></div>
          </FormProvider>
        </CardContent>
        <CardFooter className={'gap-3 w-full justify-end'}>
          <Button onClick={submit} isLoading={isSavingProfile}>
            {t('save')}
          </Button>
          <Button variant={'secondary'} onClick={() => reset()}>
            {t('cancel')}
          </Button>
        </CardFooter>
      </Card>

      <ResetPasswordDialog close={() => setPasswordDialogOpen(false)} />
    </DialogRoot>
  );
};
